import type { 
  Appointment, 
  ReceptionStats, 
  VisitStatus, 
  AppointmentsResponse,
  AppointmentsQueryParams,
  SearchResult 
} from '@app-types/reception';
import apiClient from './apiClient';

class ReceptionService {
  /**
   * Obtine lista de programari conform parametrilor
   */
  async getAppointments(params: AppointmentsQueryParams): Promise<AppointmentsResponse> {
    const response = await apiClient.get('/reception/appointments', { 
      params: {
        ...params,
        status: params.status ? params.status.join(',') : undefined
      }
    });
    return response.data.data;
  }

  /**
   * Obtine statisticile pentru o zi anume
   */
  async getStats(date: string): Promise<ReceptionStats> {
    const response = await apiClient.get('/reception/stats', { 
      params: { date } 
    });
    return response.data.data;
  }

  /**
   * Check-in pentru o programare (pacient sosit)
   */
  async checkin(appointmentId: string): Promise<Appointment> {
    const response = await apiClient.post(`/reception/appointments/${appointmentId}/checkin`);
    return response.data.data;
  }

  /**
   * Schimba statusul unei programari
   */
  async setStatus(
    appointmentId: string, 
    status: Exclude<VisitStatus, "scheduled">
  ): Promise<Appointment> {
    const response = await apiClient.post(`/reception/appointments/${appointmentId}/status`, { status });
    return response.data.data;
  }

  /**
   * Anuleaza o programare
   */
  async cancel(appointmentId: string, reason?: string): Promise<Appointment> {
    const response = await apiClient.post(`/reception/appointments/${appointmentId}/cancel`, { reason });
    return response.data.data;
  }

  /**
   * Creeaza o programare noua
   */
  async createAppointment(payload: {
    clinicId: string;
    animalId: string;
    ownerId: string;
    doctorId: string;
    serviceType: string;
    scheduledAt: string;
    notes?: string;
  }): Promise<Appointment> {
    const response = await apiClient.post('/reception/appointments', payload);
    return response.data.data;
  }

  /**
   * Cautare globala (animale, proprietari, programari)
   */
  async search(q: string): Promise<SearchResult[]> {
    const response = await apiClient.get('/reception/search', { params: { q } });
    return response.data.data;
  }

  /**
   * Obtine alertele pentru intarzieri
   */
  async getDelayAlerts(date: string, thresholdMinutes: number = 15): Promise<any[]> {
    const response = await apiClient.get('/reception/delay-alerts', { 
      params: { date, threshold: thresholdMinutes } 
    });
    return response.data.data;
  }

  /**
   * Marcheaza o programare ca finalizata din receptie
   */
  async finishVisit(appointmentId: string): Promise<Appointment> {
    return this.setStatus(appointmentId, 'finished');
  }

  /**
   * Marcheaza o programare ca "in consultatie"
   */
  async startConsultation(appointmentId: string): Promise<Appointment> {
    return this.setStatus(appointmentId, 'in_consultation');
  }
}

export const receptionService = new ReceptionService();

